import { useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { label: "The Leak", href: "#leak" },
  { label: "The Engine", href: "#engine" },
  { label: "The Founder", href: "#founder" },
  { label: "FAQ", href: "#faq" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 25 });

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 inset-x-0 z-50 border-b border-black/10 bg-white/85 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <Logo compact />
        <nav className="hidden lg:flex items-center gap-10" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm tracking-wide text-zinc-500 hover:text-champagne transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <a
            href="#book"
            data-testid="nav-cta-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-champagne px-5 py-2.5 text-sm font-medium text-white whitespace-nowrap hover:bg-champagne-soft transition-colors duration-300 group"
          >
            Book My Audit
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-pearl p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="lg:hidden border-t border-black/5 bg-white/95 backdrop-blur-xl px-6 py-6 flex flex-col gap-5"
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-base text-zinc-600 hover:text-champagne transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#book"
            onClick={() => setOpen(false)}
            className="inline-flex items-center gap-2 rounded-full bg-champagne px-5 py-3 text-sm font-medium text-white w-fit"
          >
            Book My Audit <ArrowRight size={15} />
          </a>
        </motion.div>
      )}
      <motion.div
        style={{ scaleX: progress }}
        data-testid="scroll-progress-bar"
        className="absolute bottom-0 inset-x-0 h-[2px] origin-left bg-gradient-to-r from-champagne/30 via-champagne to-champagne/30"
      />
    </motion.header>
  );
};
